/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import Tbl from '@appblocks/codegeneration/Libraries/Tbl';

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];

    if (!feature.getEnabled()) {
        return blocks;
    }

    if (feature.project.runtime !== 'tios') {
        return blocks;
    }

    const TBL_DEBUG_PRINT = feature.getSettingValue('TBL_DEBUG_PRINT');
    const TABLES = feature.getSettingValue('TABLES');

    const tablesDefined = feature.getSettingValue('TABLES');

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_tbl_error',
        template: `
sub callback_tbl_error(status as en_tbl_status_codes)
    '****callback_tbl_error****'
end sub
        `,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_tbl_fail_to_open',
        template: `
function callback_tbl_fail_to_open(byref filename as string, status as pl_fd_status_codes, filenum as byte) as no_yes
    '****callback_tbl_fail_to_open****'
end function
        `,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_tbl_field_error',
        template: `
sub callback_tbl_field_error(byref file_name as string,byref field_name as string,tbl_result as en_tbl_status_codes)
    '****callback_tbl_field_error****'
end sub
        `,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        id: 'callback_tbl_modified',
        template: `
sub callback_tbl_modified(byref file_name as string, modified_operation as en_tbl_modified_operation)
    '****callback_tbl_modified****'
end sub
        `,
    });

    const TBL_MAX_NUM_TABLES = TABLES.length;

    if (TBL_MAX_NUM_TABLES > 0) {
        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    'Tables setup
    '================================================================
    if tbl_start()<>EN_TBL_STATUS_OK then
        callback_system_error("TBL", "Failed to start")
        sys.halt()
    end if
    '-----------------------------------------------------------------`,
        });

        if (feature.getSettingValue('WEBENABLED') === '1') {
            let initCode = '';
            for (let i = 0; i < tablesDefined.length; i += 1) {
                const tableName = tablesDefined[i].getSettingValue('NAME');
                initCode += `tbl_web_set("${tableName}",true)\r\n`;
            }

            blocks.push({
                location: 'boot',
                blockType: BlockType.CODE,
                template: `
    tbl_web_start()
    dim schema_changed as boolean = false
    ${initCode}


    if tbl_schema_check(TBL_DESCRIPTOR_FILE)<>EN_TBL_STATUS_OK then
        schema_changed = YES
    end if

    if schema_changed= YES then
        callback_system_error("TBL", "Schema changed, formatting flash")
        if fd.formatj(fd.availableflashspace/4*3,32,100)<>PL_FD_STATUS_OK then
            callback_system_error("TBL", "Failed to format flash")
            sys.halt()
        end if
        if tbl_start()<>EN_TBL_STATUS_OK then
            callback_system_error("TBL", "Failed to start")
            sys.halt()
        end if
        if tbl_schema_check(TBL_DESCRIPTOR_FILE)<>EN_TBL_STATUS_OK then
            callback_system_error("TBL", "Failed to check schema")
            sys.halt()
        end if
    end if`,
            });
        }
    }

    let tables = '';
    const tablesJson = [];
    for (let i = 0; i < TABLES.length; i += 1) {
        tables += TABLES[i].getCodeBlocks()[0].template;
    }

    let TBL_MAX_TABLE_NAME_LEN = 0;
    let TBL_MAX_FIELD_NAME_LEN = 0;
    let TBL_MAX_FIELD_DISPLAY_NAME_LEN = 0;
    let TBL_MAX_TOTAL_NUM_FIELDS = 0;
    let size = 0;
    let TBL_MAX_FIELD_VALUE_LEN = 0;
    let TBL_MAX_RECORD_SIZE = 0;
    let MAX_NUM_ROWS = 1;
    for (let i = 0; i < TABLES.length; i += 1) {
        const TABLE = TABLES[i];
        const tableName = TABLE.getSettingValue('NAME');
        const tableStructure = TABLE.getSettingValue('STRUCT');
        const MAXRECORDS = TABLE.getSettingValue('MAXRECORDS');
        if (Number(MAXRECORDS) > MAX_NUM_ROWS && tableStructure === 'T') {
            MAX_NUM_ROWS = Number(MAXRECORDS);
        }

        if (tableName.length > TBL_MAX_TABLE_NAME_LEN) {
            TBL_MAX_TABLE_NAME_LEN = tableName.length;
        }

        let recordSize = 1; // active flag is 1 byte long
        const tableJson = Tbl.getTableJson(TABLE);
        for (let j = 0; j < tableJson.fields.length; j += 1) {
            const {
                name,
                fieldType,
                p1,
                p2,
                displayName,
            } = tableJson.fields[j];
            if (name.length > TBL_MAX_FIELD_NAME_LEN) {
                TBL_MAX_FIELD_NAME_LEN = name.length;
            }
            const nameLength = displayName.length > 0
                ? displayName.length : name.length;
            if (nameLength > TBL_MAX_FIELD_DISPLAY_NAME_LEN) {
                TBL_MAX_FIELD_DISPLAY_NAME_LEN = nameLength;
            }
            TBL_MAX_TOTAL_NUM_FIELDS += 1;

            size = Tbl.getFieldSize(fieldType, p1, p2);
            recordSize += size;
            if (size > TBL_MAX_FIELD_VALUE_LEN) {
                TBL_MAX_FIELD_VALUE_LEN = size;
            }
        }

        if (TBL_MAX_FIELD_NAME_LEN < 2) {
            TBL_MAX_FIELD_NAME_LEN = 2;
        }

        if (size > TBL_MAX_FIELD_VALUE_LEN) {
            TBL_MAX_FIELD_VALUE_LEN = size;
        }

        if (tableStructure === 'L') {
            let k = 2;
            // prevent unlimited loop
            while (((TBL_MAX_RECORD_SIZE % recordSize) > 0)
                && (recordSize < (TBL_MAX_RECORD_SIZE + 1))
                && (recordSize > 0) && (TBL_MAX_RECORD_SIZE < 129)) {
                if (recordSize > k) {
                    k *= 2;
                } else {
                    recordSize = k;
                }
            }
            tablesJson.push(tableJson);
        }
        if (recordSize > TBL_MAX_RECORD_SIZE) {
            TBL_MAX_RECORD_SIZE = Tbl.getProperRecordSize(recordSize);
        }
    }

    for (let i = 0; i < tablesJson.length; i += 1) {
        blocks.push({
            location: 'CGG_CONSOLE_TABLES',
            blockType: BlockType.CODE,
            template: `
            ${JSON.stringify(tablesJson[i])},`,
        });

        blocks.push({
            location: 'WEB_CONSOLE_TABLES',
            blockType: BlockType.CODE,
            template: `
            ${JSON.stringify(tablesJson[i])},`,
        });
    }


    blocks.push({
        location: 'TABLES_XTXT',
        blockType: BlockType.CODE,
        template: `
${tables}
#define TBL_DESCRIPTOR_FILE "tables.xtxt"
#define TBL_DEBUG_PRINT ${TBL_DEBUG_PRINT}
#define TBL_MAX_TABLE_NAME_LEN ${TBL_MAX_TABLE_NAME_LEN}
#define TBL_MAX_FIELD_NAME_LEN ${TBL_MAX_FIELD_NAME_LEN}
#define TBL_MAX_FIELD_DISPLAY_NAME_LEN ${TBL_MAX_FIELD_DISPLAY_NAME_LEN}
#define TBL_MAX_FIELD_VALUE_LEN ${Number(TBL_MAX_FIELD_VALUE_LEN)}
#define TBL_MAX_RECORD_SIZE ${TBL_MAX_RECORD_SIZE}
#define TBL_MAX_NUM_TABLES ${TBL_MAX_NUM_TABLES}
#define TBL_MAX_TOTAL_NUM_FIELDS ${Number(TBL_MAX_TOTAL_NUM_FIELDS)}`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        id: 'tbl_sort_declare_function',
        template:
            'declare function tbl_record_find_sorted(record_type as en_tbl_record_states, byref search_data as string(50), byref field_name as string(20), byref rec_num as word, wraparound as boolean, find_method as pl_fd_find_modes) as en_tbl_status_codes',
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.CODE,
        id: 'tbl_sort_definition_function',
        template:
            `
const MAX_NUM_ROWS = ${MAX_NUM_ROWS}

function tbl_sort_compare(byref a as string, byref b as string, field_type as en_tbl_field_types) as short
    dim a1 as dword=0
    dim b1 as dword=0
    dim w1,w2,w3 as word
    dim d as dword
    select case field_type
        case EN_TBL_FIELD_BYTE:
            a1=asc(a)
            b1=asc(b)
        case EN_TBL_FIELD_WORD:
            a1=asc(left(a,1))*256+asc(mid(a,2,1))
            b1=asc(left(b,1))*256+asc(mid(b,2,1))
        case EN_TBL_FIELD_DWORD,EN_TBL_FIELD_TIMESTAMP:
            w2=asc(left(a,1))*256+asc(mid(a,2,1))
            w3=asc(mid(a,3,1))*256+asc(mid(a,4,1))
            a1=w2*65536+w3
            w2=asc(left(b,1))*256+asc(mid(b,2,1))
            w3=asc(mid(b,3,1))*256+asc(mid(b,4,1))
            b1=w2*65536+w3
        end select
    select case field_type
        case EN_TBL_FIELD_BYTE,EN_TBL_FIELD_WORD,EN_TBL_FIELD_DWORD,EN_TBL_FIELD_TIMESTAMP:
            if a1>b1 then
                tbl_sort_compare=1
            else if a1=b1 then
                tbl_sort_compare=0
            else
                tbl_sort_compare=-1
            end if
        case EN_TBL_FIELD_FLOAT,EN_TBL_FIELD_TIME:
            dim a1 as float=0
            dim b1 as float=0
            strtobin(a1,a,4)
            strtobin(b1,b,4)
            if a1>b1 then
                tbl_sort_compare=1
            else if a1=b1 then
                tbl_sort_compare=0
            else
                tbl_sort_compare=-1
            end if
        case else:
            tbl_sort_compare=0
    end select
end function

function tbl_record_find_sorted(
    record_type as en_tbl_record_states,                        'If the search includes the records that are marked as deleted.
    byref search_data as string(50),                                'Searching criteria data.
    byref field_name as string(20),                                 'Searching criteria name.
    byref rec_num as word,                                      'Starting record number, also returns the first found record number
    wraparound as boolean,                                      'Wrap around
    find_method as pl_fd_find_modes) as en_tbl_status_codes     'find method (equal, greater, lesser, etc.)

    dim num_records as word=0
    dim count as word = 0
    dim w1,w2,w3,w4 as word
    tbl_get_num_records(num_records, no)
    if num_records=0 then
        rec_num=0
        exit function
    end if

    dim search_val as string(50) = ""
    dim tmp_rec as word=0
    dim index as integer = -1
    'sort
    dim values(MAX_NUM_ROWS) as string(4)
    dim indices(MAX_NUM_ROWS) as word
    dim i as integer
    dim key as string(4)
    dim key2 as word
    dim j as integer
    count = num_records
    i=1
    count=0

	dim ctable_info as tbl_type
	tbl_get_table_info(tbl_get_file_name(), ctable_info)
    dim field_type as en_tbl_field_types=EN_TBL_FIELD_FLOAT
    dim field_metadata as tbl_field_type
    dim num_of_fld as byte = tbl_get_num_fields(tbl_get_file_name())
    for j = 0 to num_of_fld-1
        tbl_get_field_info(tbl_get_file_name(),j,field_metadata)
        if field_metadata.field_name=field_name then
            field_type=field_metadata.field_type
            select case field_type
                case EN_TBL_FIELD_BYTE:
                    search_val=chr(val(search_data))
                case EN_TBL_FIELD_WORD:
                    w1=val(search_data)/256
                    w2=val(search_data) mod 256
                    search_val=chr(w1)+chr(w2)
                case EN_TBL_FIELD_TIMESTAMP,EN_TBL_FIELD_DWORD:
                    w2=lval(search_data)/65536
                    w3=lval(search_data) mod 65536
                    search_val=search_val+chr(w2/256)
                    search_val=search_val+chr(w2 mod 256)
                    search_val=search_val+chr(w3/256)
                    search_val=search_val+chr(w3 mod 256)
                case EN_TBL_FIELD_TIME,EN_TBL_FIELD_FLOAT:
                    dim tmp_f as float=string_to_float(search_data)
                    bintostr(search_val,tmp_f,4)
                case EN_TBL_FIELD_STRING:
                    search_val=search_data
            end select
    end if
    next j

    if (find_method=PL_FD_FIND_EQUAL OR find_method=PL_FD_FIND_NOT_EQUAL) AND field_type=EN_TBL_FIELD_STRING then
        ' go through all records and find the nth one that matches the search criteria
        i=0
        dim find_index as dword=rec_num
        rec_num=0
        if num_records=0 then
            exit function
        end if

        j=1
        while i < num_records
            tmp_rec=i+1
            
            if tbl_is_record_deleted(i)=NO then
                j=i
                tbl_record_sg(tmp_rec,EN_TBL_GET)
                tbl_field_sg(field_name,search_val,EN_TBL_GET)
                if find_method=PL_FD_FIND_EQUAL then
                    if search_val=search_data then
                        count=count+1
                    end if
                else
                    if search_val<>search_data then
                        count=count+1
                    end if
                end if
            end if
            if count=find_index then
                rec_num=tmp_rec
                exit while
            end if
            i=i+1
        wend
        exit function
    end if

	if ctable_info.structure=EN_TBL_STRUCT_TABLE then 
    while count < num_records
        if tbl_is_record_deleted(i)=NO then
            tbl_record_sg(i,EN_TBL_GET)
            indices(count)=i
            tbl_field_sg_raw(field_name,values(count))
            count=count+1
        end if
        i=i+1
    wend
    i=0
    while i < num_records-1
        j=0
        while j < num_records - i - 1
            dim result as short=tbl_sort_compare(values(j),values(j+1),field_type)
            if result=1 then
                key=values(j)
                key2=indices(j)
                values(j)=values(j+1)
                indices(j)=indices(j+1)
                values(j+1)=key
                indices(j+1)=key2
            end if
            j=j+1
        wend
        i=i+1
    wend
	end if
    'find in array
    
    index=-1
    count=0
    dim start_i, end_i, iteration_step as integer
    start_i=0
    end_i=num_records
    iteration_step=1
    if find_method=PL_FD_FIND_LESSER OR find_method=PL_FD_FIND_LESSER_EQUAL then
        start_i=num_records-1
        end_i=-1
        iteration_step=-1
    end if
    i=start_i
    while i <> end_i
		dim value as string=""
		if ctable_info.structure=EN_TBL_STRUCT_TABLE then
			value=values(i)
		else
			tbl_record_sg(i+1,EN_TBL_GET)
			tbl_field_sg_raw(field_name,value)
		end if
        dim sort_result as short=tbl_sort_compare(value,search_val,field_type)
        dim found_index as integer=-1
        select case find_method
        case PL_FD_FIND_GREATER_EQUAL:
            if sort_result=1 OR sort_result=0 then
                found_index=i
            end if
        case PL_FD_FIND_GREATER
            if sort_result=1 then
                found_index=i
            end if
        case PL_FD_FIND_LESSER:
            if sort_result=-1 then
                found_index=i
            end if
        case PL_FD_FIND_LESSER_EQUAL:
            if sort_result=-1 OR sort_result=0 then
                found_index=i
            end if
        case PL_FD_FIND_NOT_EQUAL:
            if sort_result<>0 then
                found_index=i
            end if
        case PL_FD_FIND_EQUAL:
            if sort_result=0 then
                found_index=i
            end if
        end select
        if found_index<>-1 then
            count=count+1
            if count=rec_num then
                index=found_index
                exit while
            end if
        end if
        i=i+iteration_step
    wend
    'if found, return index
    if index <> -1 then
		if ctable_info.structure=EN_TBL_STRUCT_TABLE then
			rec_num=indices(index)
		else
			rec_num=index
		end if
    else
    'if not found and wrap around, return first sorted item
        if wraparound then
            select case find_method
            case PL_FD_FIND_GREATER_EQUAL,PL_FD_FIND_GREATER:
                rec_num=indices(0)
            case PL_FD_FIND_LESSER,PL_FD_FIND_LESSER_EQUAL:
                rec_num=indices(num_records-1)
            end select
        else
            rec_num=0
        end if
    end if
end function`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        id: 'tbl_delete_old_records_declare_sub',
        template: 'declare sub tbl_delete_old_records(table_name as string)',
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.LOCATION,
        template: `
sub tbl_delete_old_records(table_name as string)
    dim log_tbl_info as tbl_type
    dim dwtemp as word
    dim count as word
    if tbl_select(table_name, table_name) = EN_TBL_STATUS_OK then
        tbl_get_table_info(table_name, log_tbl_info)
        if log_tbl_info.structure=EN_TBL_STRUCT_LIST then
            dwtemp = log_tbl_info.maxrecs * log_tbl_info.rec_size
            count = 256/log_tbl_info.rec_size
            while not(fd.filesize<dwtemp) and count>0
                tbl_record_delete(1)
                count=count-1
            wend
        end if
    end if
end sub
        `,
    });
    return blocks;
};
